import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import { orderIsFreeDutiesEligible } from "../../utils/orderIsFreeDutiesEligible";

import { RequestDutiesRefundCTA } from "./ctas";
import { TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE } from "./shared";

export const HowDutiesWorkIA: InstantAnswerBuilderFn = (
  strictLocalize,
  articles
) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription() as string[];
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize, articles)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize, articles)?.getLink?.(
          context.brandOrder
        );
      },
    },
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return ORDER_NOT_SELECTED(strictLocalize);
  }

  return orderIsFreeDutiesEligible(brandOrder)
    ? DUTIES_FREE_ORDER(strictLocalize)
    : NON_DUTIES_FREE_ORDER(strictLocalize);
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize, articles) => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return undefined;
  }

  if (!orderIsFreeDutiesEligible(brandOrder)) {
    return undefined;
  }

  return RequestDutiesRefundCTA(strictLocalize, articles);
};

const ORDER_NOT_SELECTED = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => HOW_DUTIES_WORK(strictLocalize),
  getDescription: () => [
    DUTIES_ARE_CALCULATED_ON_THE_TYPE_OF_GOODS(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => HOW_DUTIES_WORK(strictLocalize),
  getDescription: () => [
    ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const NON_DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => HOW_DUTIES_WORK(strictLocalize),
  getDescription: () => [
    ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const HOW_DUTIES_WORK = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "How duties work with Faire",
    description: {
      text: "Title of a support answer shown to retailers",
    },
  });
const DUTIES_ARE_CALCULATED_ON_THE_TYPE_OF_GOODS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "Duties are calculated based on the type of goods being imported, as determined by the tariff codes provided by the brand. Since duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full. If the actual cost of import duties and disbursement fees is greater than the duties promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is eligible for free duties promotion. Duties are calculated based on the type of goods being imported, as determined by the tariff codes provided by the brand. If duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full. If the actual cost of import duties and disbursement fees is greater than the promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is eligible for free duties",
    },
  });

const ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is not eligible for free duties promotion. Duties are calculated based on the type of goods being imported, as determined by the tariff codes provided by the brand. The carrier will send you an invoice for all import fees (including import duties and VAT), which you’ll pay in full.  If duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is not eligible for free duties",
    },
  });
